import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  Container,
  Alert,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

// Styled components for hierarchy visualization
const HierarchyContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  minHeight: '400px'
}));

const HierarchyLine = styled(Box)(({ theme, isVertical, isHorizontal, position }) => ({
  position: 'absolute',
  backgroundColor: theme.palette.grey[300],
  ...(isVertical && {
    width: '2px',
    height: '100%',
    left: position || '50px',
    top: 0
  }),
  ...(isHorizontal && {
    height: '2px',
    width: position || '20px',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%)'
  })
}));

const HierarchyItem = styled(Card)(({ theme, level, isSubordinate }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  marginLeft: level * 4,
  width: 'calc(100% - 32px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8]
  },
  ...(isSubordinate && {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    marginLeft: theme.spacing(4)
  })
}));

const DiagramWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: theme.spacing(2),
  overflowX: 'auto'
}));

const DiagramNodeRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5)
}));

const DiagramConnectorV = styled(Box)(({ theme }) => ({
  borderLeft: `2px solid ${theme.palette.divider}`,
  marginLeft: theme.spacing(1),
  paddingLeft: theme.spacing(2)
}));

const DiagramConnectorH = styled(Box)(({ theme }) => ({
  width: 16,
  height: 2,
  backgroundColor: theme.palette.divider,
  marginRight: theme.spacing(1)
}));

const DiagramPill = styled(Chip)(({ theme }) => ({
  borderRadius: 12,
  fontWeight: 600
}));

const AdminUserHierarchy = () => {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedManager, setSelectedManager] = useState('');
  const [loading, setLoading] = useState(false);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [managerFilter, setManagerFilter] = useState('');
  const [selectedEmployeeForPromotion, setSelectedEmployeeForPromotion] = useState('');
  const [showPromotionForm, setShowPromotionForm] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      if (isInitialized) return; // Prevent multiple initializations
      
      try {
        console.log('Initializing UserHierarchy component...');
        
        // Fetch users first
        await fetchUsers();
        
        // Only ensure all users are in hierarchy on first load
        await ensureAllUsersInHierarchy();
        
        // Fetch hierarchy data (this will include any newly added users)
        await fetchHierarchy();
        
        setIsInitialized(true);
        console.log('UserHierarchy component initialized successfully');
      } catch (error) {
        console.error('Error initializing data:', error);
        toast.error('Failed to initialize hierarchy data');
      }
    };

    initializeData();
  }, [isInitialized]);

  const ensureAllUsersInHierarchy = async () => {
    try {
      console.log('Ensuring all users are in UserHierarchy...');
      const response = await fetch(`${API_URL}/user-hierarchy/ensure-all-users`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Hierarchy sync result:', result);
        
        // Don't call fetchHierarchy here since it's called in the main initialization
      } else {
        console.error('Failed to ensure users in hierarchy');
      }
    } catch (error) {
      console.error('Error ensuring users in hierarchy:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/users`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        setUsers(result.users || result || []);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchy = async () => {
    try {
      setHierarchyLoading(true);
      const response = await fetch(`${API_URL}/user-hierarchy/complete-hierarchy`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        setHierarchy(result.hierarchy || []);
      } else {
        throw new Error('Failed to fetch hierarchy');
      }
    } catch (error) {
      console.error('Error fetching hierarchy:', error);
      toast.error('Failed to fetch hierarchy');
    } finally {
      setHierarchyLoading(false);
    }
  };

  const assignManager = async () => {
    if (!selectedEmployee || !selectedManager) {
      toast.error('Please select both employee and manager');
      return;
    }

    if (selectedEmployee === selectedManager) {
      toast.error('Employee cannot report to themselves');
      return;
    }

    // Only check circular dependency if hierarchy data exists
    if (hierarchy.length > 0) {
      if (wouldCreateCircularDependency(selectedEmployee, selectedManager)) {
        toast.error('This assignment would create a circular dependency! A manager cannot report to their own subordinate.');
        return;
      }
    }

    try {
      setLoading(true);
      
      // Check if user already exists in hierarchy
      const existingEntry = hierarchy.find(h => h.userId === selectedEmployee);
      
      console.log('Selected Employee ID:', selectedEmployee);
      console.log('Hierarchy data:', hierarchy);
      console.log('Existing entry found:', existingEntry);
      
      if (existingEntry) {
        // Update existing manager
        console.log('Updating existing manager for:', existingEntry.userEmail);
        const response = await fetch(`${API_URL}/user-hierarchy/user/${selectedEmployee}/manager`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            managerId: selectedManager
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update manager');
        }

        toast.success('Manager updated successfully');
      } else {
        // Add new user to hierarchy
        console.log('Adding new user to hierarchy:', selectedEmployee);
        const response = await fetch(`${API_URL}/user-hierarchy/add-user`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: selectedEmployee,
            managerId: selectedManager
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to assign manager');
        }

        toast.success('Employee assigned to manager successfully');
      }

      // Wait a moment before refreshing to ensure backend has processed the change
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh hierarchy
      await fetchHierarchy();
      
      // Reset selections
      setSelectedEmployee('');
      setSelectedManager('');
      setShowAssignmentForm(false);
      
    } catch (error) {
      console.error('Error assigning manager:', error);
      toast.error(error.message || 'Failed to assign manager');
    } finally {
      setLoading(false);
    }
  };

  // Function to check if assigning a manager would create a circular dependency
  const wouldCreateCircularDependency = (employeeId, managerId) => {
    // If managerId is null, it means top level - no circular dependency possible
    if (!managerId) return false;
    
    // Check if the new manager already reports to the employee (directly or indirectly)
    const visited = new Set();
    let currentManager = managerId;
    
    while (currentManager) {
      if (visited.has(currentManager)) {
        // Circular reference detected
        return true;
      }
      
      if (currentManager === employeeId) {
        // The new manager already reports to the employee
        return true;
      }
      
      visited.add(currentManager);
      
      // Find who the current manager reports to
      const managerEntry = hierarchy.find(h => h.userId === currentManager);
      if (!managerEntry || !managerEntry.managerId) {
        // Reached top level, no circular dependency
        break;
      }
      currentManager = managerEntry.managerId;
    }
    
    return false;
  };

  const removeFromHierarchy = async (userId) => {
    if (window.confirm('Are you sure you want to remove this user from the hierarchy?')) {
      try {
        const response = await fetch(`${API_URL}/user-hierarchy/user/${userId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to remove user');
        }

        toast.success('User removed from hierarchy successfully');
        
        // Wait a moment before refreshing to ensure backend has processed the change
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await fetchHierarchy();
      } catch (error) {
        console.error('Error removing user:', error);
        toast.error(error.message || 'Failed to remove user');
      }
    }
  };

  const promoteToTopLevel = async () => {
    if (!selectedEmployeeForPromotion) {
      toast.error('Please select an employee to promote');
      return;
    }

    if (window.confirm('Are you sure you want to promote this employee to top-level manager? This will remove them from their current manager.')) {
      try {
        setLoading(true);
        
        const response = await fetch(`${API_URL}/user-hierarchy/user/${selectedEmployeeForPromotion}/manager`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            managerId: null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to promote user');
        }

        toast.success('Employee promoted to top-level manager successfully');
        
        // Wait a moment before refreshing to ensure backend has processed the change
        await new Promise(resolve => setTimeout(resolve, 500));
        
        await fetchHierarchy();
        
        // Reset selections
        setSelectedEmployeeForPromotion('');
        setShowPromotionForm(false);
        
      } catch (error) {
        console.error('Error promoting user:', error);
        toast.error(error.message || 'Failed to promote user');
      } finally {
        setLoading(false);
      }
    }
  };

  // Helper: build quick lookup maps
  const buildLookupMaps = () => {
    const childrenByManager = new Map();
    for (const entry of hierarchy) {
      if (!childrenByManager.has(entry.managerId || 'ROOT')) {
        childrenByManager.set(entry.managerId || 'ROOT', []);
      }
      childrenByManager.get(entry.managerId || 'ROOT').push(entry);
    }
    return { childrenByManager };
  };

  // Helper: get all reports (direct and indirect) under a manager
  const getAllReportsUnder = (managerId) => {
    const { childrenByManager } = buildLookupMaps();
    const result = [];
    const stack = [...(childrenByManager.get(managerId) || [])];
    const seen = new Set();

    while (stack.length > 0) {
      const node = stack.pop();
      if (seen.has(node.userId)) continue;
      seen.add(node.userId);
      result.push(node);
      const children = childrenByManager.get(node.userId) || [];
      for (const child of children) stack.push(child);
    }

    return result;
  };

  // Derive filtered lists for rendering
  const getFilteredHierarchy = () => {
    if (!managerFilter) {
      const topLevel = hierarchy.filter(h => !h.managerId);
      const subs = hierarchy.filter(h => h.managerId);
      return { topLevel, subs };
    }
    // When a manager is selected, show them as the sole top-level and all their reports as subs
    const topLevel = hierarchy.filter(h => h.userId === managerFilter);
    const subs = getAllReportsUnder(managerFilter);
    return { topLevel, subs };
  };

  // Build nested tree for diagram (selected manager + their reports)
  const buildNestedTree = (rootManagerId) => {
    const children = hierarchy.filter(h => h.managerId === rootManagerId);
    const rootUser = users.find(u => u._id === rootManagerId);
    const rootNode = {
      id: rootManagerId,
      name: rootUser?.fullName || 'Unknown',
      email: rootUser?.email || '',
      children: []
    };
    const stack = [{ node: rootNode, id: rootManagerId }];

    while (stack.length) {
      const { node, id } = stack.pop();
      const direct = hierarchy.filter(h => h.managerId === id);
      for (const d of direct) {
        const u = users.find(x => x._id === d.userId);
        const child = { id: d.userId, name: u?.fullName || 'Unknown', email: u?.email || '', children: [] };
        node.children.push(child);
        stack.push({ node: child, id: d.userId });
      }
    }

    return rootNode;
  };

  const renderDiagramTree = (node, isRoot = true) => {
    return (
      <Box key={node.id}>
        <DiagramNodeRow>
          {!isRoot && <DiagramConnectorH />}
          <DiagramPill label={node.name} color={isRoot ? 'primary' : 'default'} />
        </DiagramNodeRow>
        {node.children && node.children.length > 0 && (
          <DiagramConnectorV>
            {node.children.map(child => renderDiagramTree(child, false))}
          </DiagramConnectorV>
        )}
      </Box>
    );
  };

  const renderHierarchyTree = () => {
    if (hierarchyLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }

    if (hierarchy.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No hierarchy data available. Start by assigning employees to managers.
        </Alert>
      );
    }

    // Prepare filtered data based on selected manager
    const { topLevel, subs } = getFilteredHierarchy();

    if (managerFilter) {
      const tree = buildNestedTree(managerFilter);
      return (
        <HierarchyContainer>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3, fontSize: '20px' }}>
            <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Organization Hierarchy
          </Typography>
          <DiagramWrapper>
            {renderDiagramTree(tree, true)}
          </DiagramWrapper>
        </HierarchyContainer>
      );
    }

    // No manager selected: render a diagram for each top-level manager
    const topLevelRoots = hierarchy.filter(h => !h.managerId);
    return (
      <HierarchyContainer>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3, fontSize: '20px' }}>
            <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Organization Hierarchy
          </Typography>
        <DiagramWrapper>
          {topLevelRoots.map(root => (
            <Box key={root.userId} sx={{ mb: 2 }}>
              {renderDiagramTree(buildNestedTree(root.userId), true)}
            </Box>
          ))}
        </DiagramWrapper>
      </HierarchyContainer>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" component="h1" color="primary" sx={{ fontSize: '30px' }}>
            <BusinessIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            User Hierarchy Management
          </Typography>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowAssignmentForm(!showAssignmentForm)}
              color="primary"
            >
              {showAssignmentForm ? 'Hide Assignment Form' : 'Assign Employee'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<SupervisorIcon />}
              onClick={() => setShowPromotionForm(!showPromotionForm)}
              color="secondary"
            >
              {showPromotionForm ? 'Hide Promotion Form' : 'Promote to Top-Level'}
            </Button>
            
            <Autocomplete
              sx={{ minWidth: 260 }}
              size="small"
              options={users}
              value={users.find(u => u._id === managerFilter) || null}
              onChange={(event, newValue) => {
                setManagerFilter(newValue ? newValue._id : '');
              }}
              getOptionLabel={(option) => `${option.fullName} (${option.email})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter: Manager"
                  placeholder="Search manager by name or email..."
                />
              )}
              renderOption={(props, user) => (
                <Box component="li" {...props} key={user._id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SupervisorIcon fontSize="small" />
                    {user.fullName} ({user.email})
                  </Box>
                </Box>
              )}
              filterOptions={(options, { inputValue }) => {
                return options.filter((option) => {
                  const searchTerm = inputValue.toLowerCase();
                  const fullName = (option.fullName || '').toLowerCase();
                  const email = (option.email || '').toLowerCase();
                  return fullName.includes(searchTerm) || email.includes(searchTerm);
                });
              }}
              noOptionsText="No managers found"
            />

            <Button
              variant="text"
              onClick={() => setManagerFilter('')}
              disabled={!managerFilter}
            >
              Clear Filter
            </Button>
            
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchHierarchy}
              disabled={hierarchyLoading}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
        
        {/* Assignment Section */}
        {showAssignmentForm && (
          <Card sx={{ mb: 4, p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontSize: '20px' }}>
              Assign Employee to Manager
            </Typography>
            
            {/* Status indicator */}
            {hierarchyLoading ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Loading hierarchy data...</strong> Please wait before making assignments.
                </Typography>
              </Alert>
            ) : hierarchy.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>No hierarchy data yet.</strong> You can create the first hierarchy by assigning employees to managers.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Hierarchy data loaded.</strong> You can now make assignments.
                </Typography>
              </Alert>
            )}
            
            {/* Debug info - remove this after fixing */}
            {/* {selectedEmployee && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Debug Info:</strong><br/>
                  Selected Employee ID: {selectedEmployee}<br/>
                  User in hierarchy: {hierarchy.find(h => h.userId === selectedEmployee) ? 'YES' : 'NO'}<br/>
                  User in users list: {users.find(u => u._id === selectedEmployee) ? 'YES' : 'NO'}
                </Typography>
              </Alert>
            )} */}
            
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  fullWidth
                  options={users}
                  value={users.find(u => u._id === selectedEmployee) || null}
                  onChange={(event, newValue) => {
                    setSelectedEmployee(newValue ? newValue._id : '');
                  }}
                  disabled={loading}
                  getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Employee"
                      placeholder="Search employee by name or email..."
                    />
                  )}
                  renderOption={(props, user) => (
                    <Box component="li" {...props} key={user._id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" />
                        {user.fullName} ({user.email})
                      </Box>
                    </Box>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) => {
                      const searchTerm = inputValue.toLowerCase();
                      const fullName = (option.fullName || '').toLowerCase();
                      const email = (option.email || '').toLowerCase();
                      return fullName.includes(searchTerm) || email.includes(searchTerm);
                    });
                  }}
                  noOptionsText="No employees found"
                />
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Autocomplete
                  fullWidth
                  options={users.filter(user => user._id !== selectedEmployee)}
                  value={users.find(u => u._id === selectedManager) || null}
                  onChange={(event, newValue) => {
                    setSelectedManager(newValue ? newValue._id : '');
                  }}
                  disabled={loading}
                  getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Manager"
                      placeholder="Search manager by name or email..."
                    />
                  )}
                  renderOption={(props, user) => (
                    <Box component="li" {...props} key={user._id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SupervisorIcon fontSize="small" />
                        {user.fullName} ({user.email})
                      </Box>
                    </Box>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) => {
                      const searchTerm = inputValue.toLowerCase();
                      const fullName = (option.fullName || '').toLowerCase();
                      const email = (option.email || '').toLowerCase();
                      return fullName.includes(searchTerm) || email.includes(searchTerm);
                    });
                  }}
                  noOptionsText="No managers found"
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
            onClick={assignManager}
            disabled={!selectedEmployee || !selectedManager || loading}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Assign'}
                </Button>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* Promotion Section */}
        {showPromotionForm && (
          <Card sx={{ mb: 4, p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontSize: '20px' }}>
              Promote Employee to Top-Level Manager
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This will make the selected employee a top-level manager (Level 0) with no manager above them. 
                They will be removed from their current manager's team.
              </Typography>
            </Alert>
            
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={8}>
                <Autocomplete
                  fullWidth
                  options={users}
                  value={users.find(u => u._id === selectedEmployeeForPromotion) || null}
                  onChange={(event, newValue) => {
                    setSelectedEmployeeForPromotion(newValue ? newValue._id : '');
                  }}
                  disabled={loading}
                  getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Employee to Promote"
                      placeholder="Search employee by name or email..."
                    />
                  )}
                  renderOption={(props, user) => (
                    <Box component="li" {...props} key={user._id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" />
                        {user.fullName} ({user.email})
                      </Box>
                    </Box>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter((option) => {
                      const searchTerm = inputValue.toLowerCase();
                      const fullName = (option.fullName || '').toLowerCase();
                      const email = (option.email || '').toLowerCase();
                      return fullName.includes(searchTerm) || email.includes(searchTerm);
                    });
                  }}
                  noOptionsText="No employees found"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={promoteToTopLevel}
                  disabled={!selectedEmployeeForPromotion || loading}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Promote to Top-Level'}
                </Button>
              </Grid>
            </Grid>
          </Card>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Hierarchy Display */}
          {renderHierarchyTree()}
      </Paper>
    </Container>
  );
};

export default AdminUserHierarchy;
